const ZIP_LOCAL_FILE_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];
const ZIP_END_SIGNATURE = [0x50, 0x4b, 0x05, 0x06];
const ZIP_CENTRAL_FILE_SIGNATURE = [0x50, 0x4b, 0x01, 0x02];
const ZIP64_END_SIGNATURE = [0x50, 0x4b, 0x06, 0x06];
const ZIP64_LOCATOR_SIGNATURE = [0x50, 0x4b, 0x06, 0x07];
const ZIP_END_MIN_BYTES = 22;
const ZIP_MAX_COMMENT_BYTES = 0xffff;
const FAILURE_NOTE_NAME = "下载失败说明.txt";

const downloadFailure = async (response) => {
  let message = "";
  try {
    const body = (await response.text()).trim();
    if (body) {
      try {
        const payload = JSON.parse(body);
        message = String(payload?.message || payload?.detail || payload?.error || "").trim();
      } catch {
        // A proxy may return HTML. Do not surface its markup as a toast.
        if (!/<[^>]+>/.test(body)) message = body.slice(0, 200);
      }
    }
  } catch {
    // Retain the HTTP status if the error body cannot be read.
  }
  const error = new Error(message || (response.status === 404
    ? "当前集没有可打包的已完成视频"
    : `视频压缩包下载失败（HTTP ${response.status}）`));
  error.status = response.status;
  return error;
};

const hasSignature = (bytes, position, signature) => signature.every(
  (byte, index) => bytes[position + index] === byte,
);

const inspectCentralDirectory = async (blob, offset, size, expectedCount) => {
  const bytes = new Uint8Array(await blob.slice(offset, offset + size).arrayBuffer());
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const decoder = new TextDecoder("utf-8");
  let cursor = 0;
  let entryCount = 0;
  let videoCount = 0;
  let hasFailureNote = false;
  while (cursor < bytes.length) {
    if (cursor + 46 > bytes.length || !hasSignature(bytes, cursor, ZIP_CENTRAL_FILE_SIGNATURE)) {
      throw new Error("视频压缩包不完整，请重试");
    }
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const next = cursor + 46 + nameLength + extraLength + commentLength;
    if (next > bytes.length) throw new Error("视频压缩包不完整，请重试");
    const fileName = decoder.decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
    if (/\.(mp4|mov|webm|m4v)$/i.test(fileName)) videoCount += 1;
    if (fileName === FAILURE_NOTE_NAME) hasFailureNote = true;
    entryCount += 1;
    cursor = next;
  }
  if (entryCount !== expectedCount) throw new Error("视频压缩包不完整，请重试");
  if (videoCount === 0) throw new Error("压缩包中没有可下载的视频");
  return { entryCount, videoCount, hasFailureNote };
};

const readZip64End = async (blob, endOffset) => {
  if (endOffset < 20) throw new Error("视频压缩包不完整，请重试");
  const locator = new Uint8Array(await blob.slice(endOffset - 20, endOffset).arrayBuffer());
  if (!hasSignature(locator, 0, ZIP64_LOCATOR_SIGNATURE)) {
    throw new Error("视频压缩包不完整，请重试");
  }
  const locatorView = new DataView(locator.buffer);
  const zip64EndOffset = Number(locatorView.getBigUint64(8, true));
  if (!Number.isSafeInteger(zip64EndOffset) || zip64EndOffset >= endOffset - 20) {
    throw new Error("视频压缩包不完整，请重试");
  }
  const record = new Uint8Array(await blob.slice(zip64EndOffset, zip64EndOffset + 56).arrayBuffer());
  if (record.length < 56 || !hasSignature(record, 0, ZIP64_END_SIGNATURE)) {
    throw new Error("视频压缩包不完整，请重试");
  }
  const view = new DataView(record.buffer);
  const entryCount = Number(view.getBigUint64(32, true));
  const centralDirectorySize = Number(view.getBigUint64(40, true));
  const centralDirectoryOffset = Number(view.getBigUint64(48, true));
  if (![entryCount, centralDirectorySize, centralDirectoryOffset].every(Number.isSafeInteger)) {
    throw new Error("视频压缩包太大，无法验证完整性");
  }
  return { entryCount, centralDirectorySize, centralDirectoryOffset, endOffset: zip64EndOffset };
};

/** Inspect only the ZIP header and at most 65 KiB of its tail, not every video byte. */
export const inspectVideoZipBlob = async (blob) => {
  if (!blob || blob.size < 4) {
    throw new Error("压缩包中没有可下载的视频");
  }
  const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  if (hasSignature(head, 0, ZIP_END_SIGNATURE)) {
    throw new Error("压缩包中没有可下载的视频");
  }
  if (!hasSignature(head, 0, ZIP_LOCAL_FILE_SIGNATURE)) {
    throw new Error("服务器返回的不是有效视频压缩包");
  }
  if (blob.size < ZIP_END_MIN_BYTES + 4) {
    throw new Error("视频压缩包不完整，请重试");
  }

  const tailSize = Math.min(blob.size, ZIP_END_MIN_BYTES + ZIP_MAX_COMMENT_BYTES);
  const tail = new Uint8Array(await blob.slice(blob.size - tailSize).arrayBuffer());
  const view = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);
  for (let offset = tail.length - ZIP_END_MIN_BYTES; offset >= 0; offset -= 1) {
    if (!hasSignature(tail, offset, ZIP_END_SIGNATURE)) continue;
    const commentLength = view.getUint16(offset + 20, true);
    if (offset + ZIP_END_MIN_BYTES + commentLength !== tail.length) continue;
    let entryCount = view.getUint16(offset + 10, true);
    let centralDirectorySize = view.getUint32(offset + 12, true);
    let centralDirectoryOffset = view.getUint32(offset + 16, true);
    const endOffset = blob.size - tailSize + offset;
    let centralEndBound = endOffset;
    if (entryCount === 0xffff || centralDirectorySize === 0xffffffff || centralDirectoryOffset === 0xffffffff) {
      const zip64 = await readZip64End(blob, endOffset);
      ({ entryCount, centralDirectorySize, centralDirectoryOffset } = zip64);
      centralEndBound = zip64.endOffset;
    }
    if (entryCount === 0) throw new Error("压缩包中没有可下载的视频");
    if (centralDirectorySize === 0 || centralDirectoryOffset + centralDirectorySize > centralEndBound) {
      throw new Error("视频压缩包不完整，请重试");
    }
    return inspectCentralDirectory(blob, centralDirectoryOffset, centralDirectorySize, entryCount);
  }
  throw new Error("视频压缩包不完整，请重试");
};

export const fetchVerifiedVideoZip = async (url, options = {}, fetcher = fetch) => {
  const response = await fetcher(url, options);
  if (!response.ok) throw await downloadFailure(response);
  let blob;
  try {
    blob = await response.blob();
  } catch {
    throw new Error("视频压缩包传输中断，请重试");
  }
  const { entryCount, videoCount, hasFailureNote } = await inspectVideoZipBlob(blob);
  return { blob, entryCount, videoCount, hasFailureNote };
};
